import { config } from '../config/env';
import { auth } from '../firebase';

// Interface für ein einzelnes CV-Design
export interface CvDesignSettings {
  // Farben
  leftColumnColor: string;
  nameTextColor: string;
  leftColumnTextColor: string;
  rightColumnTextColor: string;
  leftColumnTitleColor: string;
  rightColumnTitleColor: string;
  
  // Sichtbarkeits-Einstellungen
  showCoreCompetenciesInCv: boolean;
  showCertificationsInCv: boolean;
  showItSkillsInCv: boolean;
  showIndustriesInCv: boolean;
  showLanguagesInCv: boolean;
  showHobbiesInCv: boolean;
  showEducationInCv: boolean;
  showWorkExperienceInCv: boolean;
  showProfileInCv: boolean;
  showAdditionalInfoInCv: boolean;
  showProfileImageInCv: boolean;
  
  // Profilbild-Einstellungen
  profileImageBorderRadius: number;
  profileImageSize: string;
  profileImageWidth: number;
  profileImagePosition: { x: number; y: number };
  profileImageAlignment: string;
  profileImageBorder: boolean;
  profileImageBorderColor: string;
  profileImageBorderThickness: number;
  
  // Namensgröße
  nameFontSize: number;
  
  // Kontakt-Display-Mode
  contactDisplayMode: 'text' | 'icon';
  
  // Header-Box-Einstellungen
  topBoxColor: string;
  topBoxAlpha?: number; // Alpha-Wert für Header-Box (0-1)
  topBoxImage: string;
  
  // Linke Spalte Bild
  leftColumnImage: string;
  
  // Template-Name
  templateName: string;
}

// Interface für alle CV-Settings (mit 3 Templates)
export interface CvSettings {
  template1: CvDesignSettings;  // Modern Design
  template2: CvDesignSettings;  // Professional Design
  template3: CvDesignSettings;  // Minimal Design
  selectedTemplate: 'template1' | 'template2' | 'template3';
  lastUpdated: string;
  version: string;
}

// Standard-Design-Settings
const createDefaultDesignSettings = (templateName: string): CvDesignSettings => ({
  // Farben
  leftColumnColor: templateName === 'Professional' ? '#2e2e2e' : '#f8f6f2', // RGB(46,46,46) für Professional
  nameTextColor: '#333333',
  leftColumnTextColor: templateName === 'Professional' ? '#ffffff' : '#333333', // Weiß für Professional
  rightColumnTextColor: '#333333',
  leftColumnTitleColor: templateName === 'Professional' ? '#ffffff' : '#333333', // Weiß für Professional
  rightColumnTitleColor: '#333333',
  
  // Sichtbarkeits-Einstellungen
  showCoreCompetenciesInCv: true,
  showCertificationsInCv: true,
  showItSkillsInCv: true,
  showIndustriesInCv: true,
  showLanguagesInCv: true,
  showHobbiesInCv: true,
  showEducationInCv: true,
  showWorkExperienceInCv: true,
  showProfileInCv: true,
  showAdditionalInfoInCv: true,
  showProfileImageInCv: true,
  
  // Profilbild-Einstellungen
  profileImageBorderRadius: templateName === 'Professional' ? 0 : 50,
  profileImageSize: 'M',
  profileImageWidth: 100,
  profileImagePosition: { x: 50, y: 50 },
  profileImageAlignment: templateName === 'Professional' ? 'left' : 'center',
  profileImageBorder: false,
  profileImageBorderColor: '#333333',
  profileImageBorderThickness: 2,
  
  // Namensgröße
  nameFontSize: 36,
  
  // Kontakt-Display-Mode
  contactDisplayMode: 'text',
  
  // Header-Box-Einstellungen
  topBoxColor: templateName === 'Professional' ? '#2e2e2e' : '#f8f6f2', // Gleiche Farbe wie linke Spalte
  topBoxAlpha: 0, // Default: 100% transparent
  topBoxImage: '', // Kein Bild standardmäßig
  
  // Linke Spalte Bild
  leftColumnImage: '', // Kein Bild standardmäßig
  
  // Template-Name
  templateName
});

// Standard-CV-Settings mit 3 Templates
export const defaultCvSettings: CvSettings = {
  template1: createDefaultDesignSettings('Modern'),
  template2: createDefaultDesignSettings('Professional'),
  template3: createDefaultDesignSettings('Minimal'),
  selectedTemplate: 'template1',
  lastUpdated: new Date().toISOString(),
  version: '2.0.0'
};

// CV-Settings über Backend speichern
export const saveCvSettings = async (userId: string, settings: CvSettings): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User nicht authentifiziert');
    }

    const idToken = await user.getIdToken();
    // Entferne nicht-persistente Felder (Namensgrößen-States) vor dem Speichern
    const sanitize = (s: CvDesignSettings): Omit<CvDesignSettings, 'nameFontSize'> & { nameFontSize?: never } => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { nameFontSize, ...rest } = s;
      return rest as any;
    };

    const sanitizedSettings: CvSettings = {
      ...settings,
      template1: sanitize(settings.template1) as any,
      template2: sanitize(settings.template2) as any,
      template3: sanitize(settings.template3) as any,
    } as CvSettings;

    const settingsWithMetadata = {
      ...sanitizedSettings,
      lastUpdated: new Date().toISOString(),
      version: '2.0.0'
    };
    
    const response = await fetch(`${config.backend.saveDataUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        uid: userId,
        data: {
          cvSettings: settingsWithMetadata
        }
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

  } catch (error) {
    console.error('Fehler beim Speichern der CV-Settings:', error);
    throw error;
  }
};

// CV-Settings über Backend laden
export const loadCvSettings = async (userId: string): Promise<CvSettings> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return defaultCvSettings;
    }

    const idToken = await user.getIdToken();
    const response = await fetch(`${config.backend.userDataUrl}/${userId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`
      }
    });

    if (response.ok) {
      const result = await response.json();
      const data = result.data || {};
      
      if (data.cvSettings) {
        // Migration von alter zu neuer Struktur
        if (data.cvSettings.version === '1.0.0' || !data.cvSettings.template1) {
          return migrateToNewStructure(data.cvSettings);
        }
        return data.cvSettings;
      } else {
        return defaultCvSettings;
      }
    } else if (response.status === 404) {
      return defaultCvSettings;
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Fehler beim Laden der CV-Settings:', error);
    return defaultCvSettings;
  }
};

// Migration von alter zu neuer Struktur
const migrateToNewStructure = (oldSettings: any): CvSettings => {
  const migratedSettings = { ...oldSettings };
  
  // Erstelle 3 Templates basierend auf den alten Settings
  const baseSettings = {
    leftColumnColor: migratedSettings.leftColumnColor || '#f8f6f2',
    nameTextColor: migratedSettings.nameTextColor || '#333333',
    leftColumnTextColor: migratedSettings.leftColumnTextColor || '#333333',
    rightColumnTextColor: migratedSettings.rightColumnTextColor || '#333333',
    leftColumnTitleColor: migratedSettings.leftColumnTitleColor || '#333333',
    rightColumnTitleColor: migratedSettings.rightColumnTitleColor || '#333333',
    showCoreCompetenciesInCv: migratedSettings.showCoreCompetenciesInCv !== undefined ? migratedSettings.showCoreCompetenciesInCv : true,
    showCertificationsInCv: migratedSettings.showCertificationsInCv !== undefined ? migratedSettings.showCertificationsInCv : true,
    showItSkillsInCv: migratedSettings.showItSkillsInCv !== undefined ? migratedSettings.showItSkillsInCv : true,
    showIndustriesInCv: migratedSettings.showIndustriesInCv !== undefined ? migratedSettings.showIndustriesInCv : true,
    showLanguagesInCv: migratedSettings.showLanguagesInCv !== undefined ? migratedSettings.showLanguagesInCv : true,
    showHobbiesInCv: migratedSettings.showHobbiesInCv !== undefined ? migratedSettings.showHobbiesInCv : true,
    showEducationInCv: migratedSettings.showEducationInCv !== undefined ? migratedSettings.showEducationInCv : true,
    showWorkExperienceInCv: migratedSettings.showWorkExperienceInCv !== undefined ? migratedSettings.showWorkExperienceInCv : true,
    showProfileInCv: migratedSettings.showProfileInCv !== undefined ? migratedSettings.showProfileInCv : true,
    showAdditionalInfoInCv: migratedSettings.showAdditionalInfoInCv !== undefined ? migratedSettings.showAdditionalInfoInCv : true,
    showProfileImageInCv: migratedSettings.showProfileImageInCv !== undefined ? migratedSettings.showProfileImageInCv : true,
    profileImageBorderRadius: migratedSettings.profileImageBorderRadius || 50,
    profileImageSize: migratedSettings.profileImageSize || 'M',
    profileImageWidth: migratedSettings.profileImageWidth || 100,
    profileImagePosition: migratedSettings.profileImagePosition || { x: 50, y: 50 },
    profileImageAlignment: migratedSettings.profileImageAlignment || 'center',
    profileImageBorder: migratedSettings.profileImageBorder || false,
    profileImageBorderColor: migratedSettings.profileImageBorderColor || '#333333',
    profileImageBorderThickness: migratedSettings.profileImageBorderThickness || 2,
    nameFontSize: migratedSettings.nameFontSize || 36,
    contactDisplayMode: migratedSettings.contactDisplayMode || 'text',
    topBoxColor: migratedSettings.topBoxColor || migratedSettings.leftColumnColor || '#f8f6f2', // Fallback auf leftColumnColor
    topBoxAlpha: migratedSettings.topBoxAlpha !== undefined ? migratedSettings.topBoxAlpha : 0, // Default: 100% transparent
    topBoxImage: migratedSettings.topBoxImage || '', // Kein Bild standardmäßig
    leftColumnImage: migratedSettings.leftColumnImage || '' // Kein Bild standardmäßig
  };

  return {
    template1: { ...baseSettings, templateName: 'Modern' },
    template2: { ...baseSettings, templateName: 'Professional' },
    template3: { ...baseSettings, templateName: 'Minimal' },
    selectedTemplate: 'template1',
    lastUpdated: new Date().toISOString(),
    version: '2.0.0'
  };
};

// CV-Settings über Backend aktualisieren
export const updateCvSettings = async (userId: string, updates: Partial<CvSettings>): Promise<void> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User nicht authentifiziert');
    }

    const idToken = await user.getIdToken();
    // Entferne nicht-persistente Felder auch bei Updates
    const stripNameFields = (part: any) => {
      if (!part) return part;
      const clone = { ...part };
      if (clone.template1) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { nameFontSize, ...rest } = clone.template1;
        clone.template1 = rest;
      }
      if (clone.template2) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { nameFontSize, ...rest } = clone.template2;
        clone.template2 = rest;
      }
      if (clone.template3) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { nameFontSize, ...rest } = clone.template3;
        clone.template3 = rest;
      }
      return clone;
    };

    const updateData = {
      ...stripNameFields(updates),
      lastUpdated: new Date().toISOString()
    };
    
    const response = await fetch(`${config.backend.saveDataUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        uid: userId,
        data: {
          cvSettings: updateData
        }
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

  } catch (error) {
    console.error('Fehler beim Aktualisieren der CV-Settings:', error);
    throw error;
  }
};

// CV-Settings über Backend zurücksetzen
export const resetCvSettings = async (userId: string): Promise<void> => {
  try {
    await saveCvSettings(userId, defaultCvSettings);
  } catch (error) {
    console.error('Fehler beim Zurücksetzen der CV-Settings:', error);
    throw error;
  }
};

// Template-spezifische Settings zurücksetzen
export const resetTemplateSettings = async (userId: string, templateId: 'template1' | 'template2' | 'template3'): Promise<void> => {
  try {
    const currentSettings = await loadCvSettings(userId);
    const updatedSettings = {
      ...currentSettings,
      [templateId]: createDefaultDesignSettings(
        templateId === 'template1' ? 'Modern' : 
        templateId === 'template2' ? 'Professional' : 'Minimal'
      )
    };
    await saveCvSettings(userId, updatedSettings);
  } catch (error) {
    console.error('Fehler beim Zurücksetzen der Template-Settings:', error);
    throw error;
  }
};